import React, { useEffect, useRef, useState } from "react";

import { Bell, CheckCheck, Users, PlayCircle, Megaphone } from "lucide-react";

import { useNavigate, useLocation } from "react-router-dom";

import { useNotifications } from "../hooks/useNotification";

import "./NotificationBell.css";

const NotificationIcon = ({ sourceType }) => {
  if (sourceType === "group") {
    return <Users size={19} strokeWidth={1.8} />;
  }

  if (sourceType === "channel") {
    return <PlayCircle size={19} strokeWidth={1.8} />;
  }

  return <Megaphone size={19} strokeWidth={1.8} />;
};

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
};

const NotificationBell = ({ onOpen, profileMenuOpen = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const notificationRef = useRef(null);
  const triggerRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const {
    notifications,
    unreadCount,
    loading,
    error,
    getNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications(true);

  useEffect(() => {
    if (!isOpen) return;

    void getNotifications();
  }, [isOpen, getNotifications]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (profileMenuOpen) {
      setIsOpen(false);
    }
  }, [profileMenuOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);

      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      onOpen?.();
    }

    setIsOpen((previous) => !previous);
  };

  const handleNotificationClick = async (notification) => {
    const isUnread = Number(notification.isRead) === 0;

    if (isUnread) {
      const success = await markAsRead(notification.notificationId);

      if (!success) {
        return;
      }
    }

    setIsOpen(false);

    const path = notification.targetPath;

    if (
      typeof path === "string" &&
      path.startsWith("/") &&
      !path.startsWith("//") &&
      !path.includes("\\")
    ) {
      navigate(path);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  return (
    <div
      ref={notificationRef}
      className="sidebar__item-wrapper sidebar__notification-wrapper"
    >
      <button
        ref={triggerRef}
        type="button"
        className={`sidebar__link sidebar__notification-trigger ${
          isOpen ? "sidebar__link--active" : ""
        }`}
        onClick={handleToggle}
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={isOpen ? "sidebar-notification-panel" : undefined}
        title="Notifications"
      >
        <span className="sidebar__link-icon">
          <Bell size={22} strokeWidth={1.8} />

          {unreadCount > 0 && (
            <span className="sidebar__notification-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </span>

        <span className="sidebar__label">Notification</span>
      </button>

      {isOpen && (
        <div
          id="sidebar-notification-panel"
          className="sidebar__dropdown sidebar__notification-panel"
          role="region"
          aria-label="Your notifications"
        >
          <div className="sidebar__notification-header">
            <div className="sidebar__notification-heading">
              <h3>Notifications</h3>

              <span>{unreadCount} unread</span>
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                className="sidebar__notification-mark-all"
                onClick={handleMarkAllRead}
                title="Mark all as read"
              >
                <CheckCheck size={17} />

                <span>Mark all read</span>
              </button>
            )}
          </div>

          <div className="sidebar__notification-list">
            {loading && notifications.length === 0 ? (
              <div className="sidebar__notification-empty">
                Loading notifications...
              </div>
            ) : error ? (
              <div className="sidebar__notification-error" role="alert">
                {error}
              </div>
            ) : notifications.length === 0 ? (
              <div className="sidebar__notification-empty">
                <Bell size={26} strokeWidth={1.5} />

                <span>No notifications yet.</span>
              </div>
            ) : (
              notifications.map((notification) => {
                const isUnread = Number(notification.isRead) === 0;

                return (
                  <button
                    key={notification.notificationId}
                    type="button"
                    className={`sidebar__notification-item ${
                      Number(notification.isRead) === 0
                        ? "sidebar__notification-item--unread"
                        : ""
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <span className="sidebar__notification-item-icon">
                      <NotificationIcon sourceType={notification.sourceType} />
                    </span>

                    <span className="sidebar__notification-item-content">
                      <strong>{notification.title}</strong>

                      <span>{notification.message}</span>
                    </span>

                    {Number(notification.isRead) === 0 && (
                      <span className="sidebar__notification-unread-dot" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="sidebar__notification-footer">
            Latest notifications
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
